import { CollabCard } from "@/components/collabs/collab-card"
import { ContributorsStrip } from "@/components/collabs/contributors-strip"
import { ConversationCard } from "@/components/collabs/conversation-card"
import { ProgressIndicator } from "@/components/collabs/progress-indicator"
import { Avatar } from "@/components/ui/avatar"

import {
  BRIEF_FIXTURE,
  FEATURED_CONTRIBUTORS,
  TRACK_PROMPT_FIXTURE,
  WHY_NOTES,
} from "../_fixtures"

import { ManualSection, Plate } from "./primitives"

// Signature patterns: the compositions that make a screen ours
// rather than any other Batyr product. Rendered with the real product
// components from components/briefs/, fed by fixtures.
export function PatternsSection() {
  const featured = WHY_NOTES[0]

  return (
    <ManualSection
      id="patterns"
      title="Signature Patterns"
      lede="The kit is shared; these compositions are ours. They make attribution a first-class element, lift the contributor's note above the media it points to, and frame progress as a page filling with people. A brief is something a peer made for you, never an assignment."
    >
      <div className="grid items-start gap-6 lg:grid-cols-5">
        <Plate
          title="The why-note"
          note="The note outranks the embed. The track sits under the story as supporting metadata."
          className="lg:col-span-3"
        >
          <blockquote className="flex flex-col gap-4">
            <p className="text-primary max-w-prose text-lg text-pretty">
              &ldquo;{featured.note}&rdquo;
            </p>
            <footer className="flex items-center gap-3">
              <Avatar size="md" name={featured.contributor} />
              <div className="flex flex-col">
                <span className="text-primary text-sm font-semibold">
                  {featured.contributor}
                </span>
                <span className="text-tertiary text-sm">
                  on{" "}
                  <a
                    href="#patterns"
                    className="text-brand-secondary hover:text-brand-secondary_hover outline-focus-ring rounded-xs underline underline-offset-2 focus-visible:outline-2 focus-visible:outline-offset-2"
                  >
                    {featured.track}
                  </a>{" "}
                  — {featured.artist}
                </span>
              </div>
            </footer>
          </blockquote>
        </Plate>

        <Plate
          title="Progress as a filling page"
          note="One bar per brief, framed as a page filling with people. No checkmarks and no module numbering; a brief is something a peer made for you."
          className="lg:col-span-2"
        >
          <ProgressIndicator progress={BRIEF_FIXTURE.progress} />
          <p className="text-tertiary text-sm">
            &ldquo;Three slots left, and this one is nearly a playlist.&rdquo;
            The curation language keeps the energy communal and well clear of
            LMS.
          </p>
        </Plate>
      </div>

      <div className="grid items-start gap-6 lg:grid-cols-3">
        <Plate
          title="The brief card"
          note="Focal-point crop, visible tags, byline stack, suggestion counts, all server-resolved."
          importPath="@/components/collabs/collab-card"
          bodyClassName="bg-secondary"
        >
          <div className="mx-auto w-full max-w-sm">
            <CollabCard brief={BRIEF_FIXTURE} />
          </div>
        </Plate>

        <Plate
          title="The byline, expanded"
          note="When in doubt, give the byline more presence. Roles use editorial language; taglines surface on hover."
          importPath="@/components/briefs/contributors-strip"
        >
          <ContributorsStrip featured={FEATURED_CONTRIBUTORS} />
        </Plate>

        <Plate
          title="The conversation card"
          note="A slot articulated as a question you'd deal across a table: position as the rank, content type as the suit, the prompt as the ask. The deck replaces the slot list."
          importPath="@/components/briefs/conversation-card"
          bodyClassName="bg-surface-grouped"
        >
          <div className="mx-auto w-64">
            <ConversationCard prompt={TRACK_PROMPT_FIXTURE} />
          </div>
        </Plate>
      </div>
    </ManualSection>
  )
}
